import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Newspaper, RefreshCw, Loader2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState, PageLoading, PageHeader } from "@/components/shared";
import api from "@/lib/api";

const FLAG = { DE: "🇩🇪", SI: "🇸🇮", HR: "🇭🇷", SK: "🇸🇰", AT: "🇦🇹" };

export default function ComplianceNews() {
  const { t } = useTranslation();
  const [news, setNews] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/compliance-news").then((r) => setNews(r.data.news));
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setBusy(true);
    try {
      await api.post("/admin/compliance-news/refresh");
      toast.success(t("ai.refreshStarted"));
      const before = news?.length || 0;
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const r = await api.get("/admin/compliance-news");
          setNews(r.data.news);
          if ((r.data.news?.length || 0) > before || tries >= 12) { setBusy(false); return; }
        } catch { /* keep polling */ }
        setTimeout(poll, 12000);
      };
      setTimeout(poll, 12000);
    } catch { toast.error("Error"); setBusy(false); }
  };

  return (
    <div data-testid="admin-compliance-news">
      <PageHeader icon={Newspaper} title={t("ai.newsTitle")} subtitle={t("ai.newsSub")} action={
        <Button onClick={refresh} disabled={busy} data-testid="news-refresh-btn" className="rounded-full bg-navy text-white hover:bg-navy-muted">
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />} {busy ? t("ai.refreshing") : t("ai.refresh")}
        </Button>
      } />
      {news === null ? <PageLoading /> : news.length === 0 ? <EmptyState icon={Newspaper} title={t("ai.newsEmpty")} /> : (
        <div className="grid gap-4 md:grid-cols-2">
          {news.map((n) => (
            <div key={n.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`news-item-${n.id}`}>
              <div className="mb-2 flex items-center gap-2 text-xs">
                <span className="text-base">{FLAG[n.country] || "🏳️"}</span>
                {n.category && <span className="rounded-full bg-cyan/10 px-2 py-0.5 font-semibold uppercase tracking-wider text-cyan-dark">{n.category}</span>}
                {n.published_at && <span className="text-slate-400">{n.published_at}</span>}
              </div>
              <div className="font-heading text-base font-bold text-navy">{n.title}</div>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{n.summary}</p>
              <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
                <span>{n.source_name}</span>
                {n.source_url && <a href={n.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-cyan-dark hover:underline">{t("tax.viewSource")} <ExternalLink className="h-3 w-3" /></a>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
